import DOMPurify from "isomorphic-dompurify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/tr";
dayjs.extend(relativeTime);
dayjs.locale("tr");

export const jsonTryParse = (arg: string): any | null => {
  let result: unknown = null;

  try {
    result = JSON.parse(arg);
  } catch (e) {
    console.error(e);
  }

  return result;
};

export const jsonTryStringify = (arg: unknown): string | null => {
  let result: string | null = null;

  try {
    result = JSON.stringify(arg);
  } catch (e) {
    console.error(e);
  }

  return result;
};

export const parseJwt = (token: string): Record<string, null> | null => {
  const base64Url: string = token.split(".")[1];
  const base64: string = base64Url?.replace(/-/g, "+")?.replace(/_/g, "/");

  const jsonPayload = decodeURIComponent(
    window
      .atob(base64 || "")
      ?.split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join(""),
  );

  return jsonTryParse(jsonPayload);
};

export const isNullOrEmptyOrUndefined = (arg: any): boolean => {
  return isUndefined(arg) || isNull(arg) || isEmpty(arg);
};

export const isNullOrUndefined = (arg: any): boolean => {
  return isNull(arg) || isUndefined(arg);
};

export const isBoolean = (arg: any): boolean => {
  return typeof arg === "boolean";
};

export const isNull = (arg: any): boolean => {
  return arg === null;
};

export const isInfinite = (arg: any): boolean => {
  return !isFinite(arg);
};

export const isEmpty = (arg: any): boolean => {
  if (isObject(arg)) {
    return isEmptyObject(arg);
  }

  if (isArray(arg)) {
    return isEmptyArray(arg);
  }

  return isEmptyString(arg);
};

export const isFalsy = (arg: any): boolean => {
  return arg === false;
};

export const isTruthy = (arg: any): boolean => {
  return arg === true;
};

export const isEmptyString = (arg: any): boolean => {
  return arg === "";
};

export const isEmptyArray = (arg: []): boolean => {
  return Boolean(arg?.length);
};

export const isEmptyObject = (arg: any): boolean => {
  for (const key in arg) {
    if (arg.hasOwnProperty!(key)) {
      return false;
    }
  }

  return true;
};

export const isNumber = (arg: any): boolean => {
  return typeof arg === "number";
};

export const isString = (arg: any): boolean => {
  return typeof arg === "string";
};

export const isSymbol = (arg: any): boolean => {
  return typeof arg === "symbol";
};

export const isUndefined = (arg: any): boolean => {
  return arg === undefined;
};

export const isRegExp = (arg: any): boolean => {
  return arg instanceof RegExp;
};

export const isObject = (arg: any): boolean => {
  return (
    !isNull(arg) && !isDate(arg) && !isRegExp(arg) && typeof arg === "object"
  );
};

export const isDate = (arg: any): boolean => {
  return arg instanceof Date;
};

export const isFunction = (arg: any): boolean => {
  return typeof arg === "function";
};

export const isPrimitive = (arg: any): boolean => {
  return isNull(arg) || (!isObject(arg) && !isFunction(arg));
};

export const isArray: any = Array.isArray;

/* Additional tools */
export const assetPath = (filename: string, root?: string): string => {
  if (filename?.includes("://")) return filename;

  return `${root ? root : process.env.ASSET_SERVER_ADDRESS}/${filename}`;
};

export const purify = (html?: string): { __html: string } => {
  return {
    __html: DOMPurify.sanitize(html ?? "", {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: [
        "allow",
        "allowfullscreen",
        "frameborder",
        "scrolling",
        "target",
      ],
    }),
  };
};

export const formatRelativeTime = (timestamp: string): string => {
  const now = dayjs();
  const date = dayjs(timestamp);
  const diffInSeconds = now.diff(date, "second");

  if (diffInSeconds < 60) {
    return "Az önce";
  }

  return date.fromNow();
};

export const removeEmojis = (text: string): string => {
  return text
    .replace(/[\p{Emoji}]/gu, "")
    .replace(/[\uFE0E\uFE0F]/g, "")
    .replace(/[\u{1F3FB}-\u{1F3FF}]/gu, "")
    .replace(/:[a-zA-Z0-9_+-]+:|<[/]?3|&lt;[/]?3|♥|❤|[#*0-9]\u20E3/g, "");
};

export const removeLaTeX = (text: string): string => {
  const displayMath = /\$\$[^$]*\$\$/g; // $$ ... $$
  const displayMathBrackets = /\\\[.*?\\\]/g; // \[ ... \]
  const inlineMath = /\$[^$]*\$/g; // $ ... $

  return text
    .replace(displayMath, "")
    .replace(displayMathBrackets, "")
    .replace(inlineMath, "");
};

export const removeEmojisAndLatex = (text: string): string => {
  text = removeEmojis(text);
  text = removeLaTeX(text);

  return text;
};

export const removeSpaces = (str: string): string => str.replace(/\s+/g, "");

export const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes?.toString()?.padStart(2, "0")}:${remainingSeconds?.toString()?.padStart(2, "0")}`;
};

export const optimizeKatexInResponse = (text: string): string => {
  let processed = text.replace(/\\\[([\s\S]*?)\\\]/g, (_, p1) => {
    return `\n\n$$\n${p1.trim()}\n$$\n`;
  });

  processed = processed.replace(
    /([\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}★])/gu,
    " $1 ",
  );

  return processed;
};

export const scrollWithOffset = (el: HTMLElement) => {
  const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
  const yOffset = -90;
  window.scrollTo({ top: yCoordinate + yOffset, behavior: "smooth" });
};

export const binCheck = async (params: {
  bincode: string;
  operation: string;
  packageIds: string;
  userId: string;
}) => {
  const response = await fetch(process.env.BINCHECK_API_URL!, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: jsonTryStringify(params),
  });

  if (!response.ok) {
    throw new Error("Sunucu hatası: " + response.status);
  }

  return response.json(); // JSON veriyi dön
};
